/**
 * Integration Tab Component (Enhanced)
 *
 * Displays integration patterns with rich details and documentation links.
 * Features expandable cards, visual indicators, and comprehensive information display.
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  AlertCircle,
  Network,
  FileText,
  ExternalLink,
  BookOpen,
  FileCode,
  Video,
  HelpCircle,
  Sparkles,
  Search,
} from 'lucide-react';
import {
  getIntegrationPatterns,
  getDocumentationLinks,
  discoverIntegrationPatterns,
} from '../../lib/api/enterpriseSystemsApi';
import { toast } from 'sonner';
import IntegrationPatternCard from './IntegrationPatternCard';
import IntegrationPatternDetails from './IntegrationPatternDetails';

interface IntegrationTabProps {
  systemId: string;
}

const IntegrationTab: React.FC<IntegrationTabProps> = ({ systemId }) => {
  const queryClient = useQueryClient();
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [expandedPatternId, setExpandedPatternId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch integration patterns
  const {
    data: patterns = [],
    isLoading: patternsLoading,
    error: patternsError,
    refetch: refetchPatterns,
  } = useQuery({
    queryKey: ['integration-patterns', systemId],
    queryFn: () => getIntegrationPatterns(systemId),
  });

  // Fetch documentation links
  const {
    data: docs = [],
    isLoading: docsLoading,
    error: docsError,
  } = useQuery({
    queryKey: ['documentation-links', systemId],
    queryFn: () => getDocumentationLinks(systemId),
  });

  // Discovery mutation
  const discoveryMutation = useMutation({
    mutationFn: () => discoverIntegrationPatterns(systemId),
    onSuccess: (data) => {
      setIsDiscovering(true);
      toast.success(`Integration pattern discovery started! Job ID: ${data.job_id}`, {
        description: `This will take approximately ${data.estimated_time_minutes} minutes.`,
        duration: 5000,
      });

      // Poll for results after estimated time
      setTimeout(() => {
        refetchPatterns();
        setIsDiscovering(false);
        toast.info('Checking for new integration patterns...');
      }, data.estimated_time_minutes * 60 * 1000);
    },
    onError: (error: any) => {
      setIsDiscovering(false);
      toast.error('Failed to start integration pattern discovery', {
        description: error.message || 'Please try again later.',
      });
    },
  });

  const handleDiscoverPatterns = () => {
    discoveryMutation.mutate();
  };

  const handleTogglePattern = (patternId: string) => {
    setExpandedPatternId((current) => (current === patternId ? null : patternId));
  };

  // Filter patterns by search query
  const filteredPatterns = useMemo(() => {
    if (!searchQuery.trim()) return patterns;

    const query = searchQuery.toLowerCase();
    return patterns.filter(
      (pattern: any) =>
        pattern.pattern_name?.toLowerCase().includes(query) ||
        pattern.pattern_type?.toLowerCase().includes(query) ||
        pattern.business_value?.toLowerCase().includes(query) ||
        pattern.data_flow?.toLowerCase().includes(query)
    );
  }, [patterns, searchQuery]);

  const isLoading = patternsLoading || docsLoading;
  const error = patternsError || docsError;

  /**
   * Get documentation icon by type
   */
  const getDocIcon = (docType: string) => {
    switch (docType?.toLowerCase()) {
      case 'api_reference':
        return FileCode;
      case 'developer_guide':
        return BookOpen;
      case 'tutorial':
        return Video;
      case 'user_manual':
        return FileText;
      default:
        return HelpCircle;
    }
  };

  /**
   * Get documentation type label
   */
  const getDocTypeLabel = (docType: string | undefined) => {
    if (!docType) return 'Unknown';
    return docType
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
        <span className="ml-3 text-neutral-600">Loading integration details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error Loading Integration Data</h3>
            <p className="text-sm text-red-700 mt-1">{(error as Error).message}</p>
          </div>
        </div>
      </div>
    );
  }

  const hasNoData = patterns.length === 0 && docs.length === 0;

  if (hasNoData) {
    return (
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-12 text-center">
        <Network className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-neutral-900 mb-2">
          No Integration Data Found
        </h3>
        <p className="text-neutral-600 mb-6">
          Run AI research to discover integration patterns and documentation
        </p>
        <button
          onClick={handleDiscoverPatterns}
          disabled={isDiscovering || discoveryMutation.isPending}
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-neutral-400 disabled:cursor-not-allowed transition-colors"
        >
          {isDiscovering || discoveryMutation.isPending ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Discovering Patterns...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              Discover Integration Patterns
            </>
          )}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">Integration & Documentation</h2>
          <p className="text-sm text-neutral-600 mt-1">
            Integration patterns and official documentation links
          </p>
        </div>
        <button
          onClick={handleDiscoverPatterns}
          disabled={isDiscovering || discoveryMutation.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:bg-neutral-400 disabled:cursor-not-allowed transition-colors"
        >
          {isDiscovering || discoveryMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Discovering...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Discover Patterns
            </>
          )}
        </button>
      </div>

      {/* Integration Patterns */}
      {patterns.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-neutral-900">
              Integration Patterns ({filteredPatterns.length})
            </h3>

            {/* Search Box */}
            {patterns.length > 3 && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search patterns..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 w-64 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            )}
          </div>

          {filteredPatterns.length === 0 ? (
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-8 text-center">
              <p className="text-neutral-600">No patterns match your search.</p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Clear search
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredPatterns.map((pattern: any) => {
                const isExpanded = expandedPatternId === pattern.pattern_id;

                return (
                  <div key={pattern.pattern_id}>
                    <IntegrationPatternCard
                      pattern={pattern}
                      isExpanded={isExpanded}
                      onToggle={() => handleTogglePattern(pattern.pattern_id)}
                    />

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="mt-0 border-x border-b border-neutral-200 rounded-b-lg bg-white">
                        <IntegrationPatternDetails pattern={pattern} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Documentation Links */}
      {docs.length > 0 && (
        <div>
          <h3 className="text-xl font-semibold text-neutral-900 mb-4">
            Official Documentation ({docs.length})
          </h3>
          <div className="space-y-3">
            {docs.map((doc: any) => {
              const DocIcon = getDocIcon(doc.documentation_type);

              return (
                <a
                  key={doc.link_id}
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-white border border-neutral-200 rounded-lg p-5 hover:shadow-md hover:border-primary-300 transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2.5 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                      <DocIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <h4 className="text-base font-semibold text-neutral-900 group-hover:text-primary-600 transition-colors">
                          {doc.title}
                        </h4>
                        <ExternalLink className="w-4 h-4 text-neutral-400 group-hover:text-primary-600 flex-shrink-0" />
                      </div>
                      {doc.description && (
                        <p className="text-sm text-neutral-600 mb-2">{doc.description}</p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-neutral-500">
                        <span className="px-2 py-1 bg-neutral-100 text-neutral-700 rounded">
                          {getDocTypeLabel(doc.link_type)}
                        </span>
                        {doc.access_level && (
                          <span
                            className={`px-2 py-1 rounded ${
                              doc.access_level === 'public'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-orange-100 text-orange-700'
                            }`}
                          >
                            {doc.access_level === 'public' ? '🌐 Public' : '🔒 Restricted'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationTab;
